import {
  collectionGroup,
  deleteField,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  Timestamp,
  type DocumentReference,
  type FieldValue,
} from 'firebase/firestore';
import { db } from '../../config';
import { firestoreBatchLimit, masterStatus } from '../../../../constants/enum';
import {
  type ColumnMapping,
  type MasterFilterKey,
  type MasterSearchKey,
} from '../../../../types/master';
import { type DictionaryColumnMapping } from '../../../../types/dictionary';

/**
 * 削除された項目を参照しているマスタ照合・辞書変換の設定を解除する（#3344）。
 *
 * 扱うのは「欠けても照合が成立する」参照:
 *   - filter_keys[]      : 該当要素を配列から除去（空配列になってもマッピングは残す）
 *   - column_mappings[]  : 該当要素を配列から除去
 *   - scope_item_id/label: フィールドを削除（照合履歴が全体スコープに戻る）
 *
 * 照合キー（search_key / search_item_id）には触らない。欠けたマッピングは照合が
 * 成立しないが、勝手に削除せず残して利用者に作り直してもらう（仕様書 §2.1）。
 * 項目削除ボタン経由ならブロックされるが、JSON取り込み・AI再読取は itemList を
 * 丸ごと差し替えるため到達しうる。到達分は mappingId を返し、呼び出し側で通知させる。
 *
 * Cloud SQL の master_field_mappings は触らない（仕様書 §2.3）。
 * source_field_name は照合キーのラベルであり、照合キーには手を付けないため整合が崩れない。
 */

/** 解除処理の結果 */
export type CleanupItemReferencesResult = {
  /** 更新したマッピング件数（マスタ＋辞書） */
  updatedCount: number;
  /**
   * 照合キーが削除済み項目を指していたマッピングのID。
   * 照合が成立しない状態なので、呼び出し側で利用者に設定の見直しを促す。
   */
  skippedSearchKeyMappingIds: string[];
};

type MappingUpdates = Record<
  string,
  string | ColumnMapping[] | MasterFilterKey[] | DictionaryColumnMapping[] | Timestamp | FieldValue
>;

/** バッチに積む1件分の更新 */
type PendingUpdate = {
  ref: DocumentReference;
  updates: MappingUpdates;
};

/**
 * 指定パターンのマスタ照合・辞書変換マッピングから、保存後の item_list に
 * 存在しない item_id の参照を除去する。
 *
 * 「残っている項目」を渡して Firestore 側の参照と突き合わせる形にしているのは、
 * 呼び出し側が保持するマスタ・辞書の state に依存しないため。
 * state の取得が失敗していても解除は正しく動く。
 *
 * @param patternId    対象パターンID
 * @param savedItemIds 保存後の item_list に存在する item_id の集合（collectItemIds の戻り値）
 * @param companyId    対象企業ID。他社ドキュメントを誤ってバッチに積まないための検証に使う
 */
export const cleanupItemReferences = async (
  patternId: string,
  savedItemIds: ReadonlySet<string>,
  companyId: string,
): Promise<CleanupItemReferencesResult> => {
  const result: CleanupItemReferencesResult = {
    updatedCount: 0,
    skippedSearchKeyMappingIds: [],
  };

  if (!patternId || !companyId) return result;

  // 保存後の項目が 0 件になるのは異常（バリデーションで防がれている）。
  // この状態で走らせると全参照を孤児と見なして全マッピングを空にしてしまうため、何もしない。
  if (savedItemIds.size === 0) return result;

  const isOrphaned = (itemId: string | undefined): boolean => !!itemId && !savedItemIds.has(itemId);

  const master = await collectMasterUpdates(patternId, isOrphaned, companyId);
  const dictionary = await collectDictionaryUpdates(patternId, isOrphaned, companyId);

  await commitInBatches([...master.updates, ...dictionary.updates]);

  result.updatedCount = master.updates.length + dictionary.updates.length;
  result.skippedSearchKeyMappingIds = [
    ...master.skippedSearchKeyMappingIds,
    ...dictionary.skippedSearchKeyMappingIds,
  ];

  return result;
};

/** item_id が保存後の item_list から消えているか判定する関数 */
type IsOrphaned = (itemId: string | undefined) => boolean;

type CollectResult = {
  updates: PendingUpdate[];
  skippedSearchKeyMappingIds: string[];
};

/** マスタ照合マッピングの更新内容を集める */
const collectMasterUpdates = async (
  patternId: string,
  isOrphaned: IsOrphaned,
  companyId: string,
): Promise<CollectResult> => {
  const snapshot = await getDocs(
    query(collectionGroup(db, 'master_mappings'), where('pattern_id', '==', patternId)),
  );

  const updates: PendingUpdate[] = [];
  const skippedSearchKeyMappingIds: string[] = [];

  for (const mappingDoc of snapshot.docs) {
    const data = mappingDoc.data();

    // 論理削除済みのマッピングは照合に使われないため触らない（#2896 の方針でデータは残す）
    if (data.status === masterStatus.deleted) continue;
    if (!(await belongsToCompany(mappingDoc.ref, companyId))) continue;

    // 照合キーが孤児のマッピングは照合が成立しない。記録して利用者に知らせるが、
    // 残った他の孤児参照（反映先・絞り込み・分離キー）は掃除する。
    // 掃除を諦めると、壊れたマッピングに孤児参照が溜まり続ける。
    const searchKey = data.search_key as MasterSearchKey | undefined;
    const searchItemId = searchKey?.item_id || (data.search_item_id as string | undefined) || '';
    if (isOrphaned(searchItemId)) {
      skippedSearchKeyMappingIds.push(mappingDoc.id);
    }

    const mappingUpdates: MappingUpdates = {};

    // 絞り込みキー: 該当要素を除去。空配列になってもマッピングは残す（照合キーで成立するため）
    const filterKeys = (data.filter_keys as MasterFilterKey[] | undefined) ?? [];
    const nextFilterKeys = filterKeys.filter(key => !isOrphaned(key?.item_id));
    if (nextFilterKeys.length !== filterKeys.length) {
      mappingUpdates.filter_keys = nextFilterKeys;
    }

    // 反映先: 該当要素を除去
    const columnMappings = (data.column_mappings as ColumnMapping[] | undefined) ?? [];
    const nextColumnMappings = columnMappings.filter(mapping => !isOrphaned(mapping?.item_id));
    if (nextColumnMappings.length !== columnMappings.length) {
      mappingUpdates.column_mappings = nextColumnMappings;
    }

    // 履歴分離キー: フィールドごと削除する。
    // 空文字を入れると「スコープ指定あり・値なし」と区別できないため deleteField を使う。
    const scopeItemId = data.scope_item_id as string | undefined;
    if (isOrphaned(scopeItemId)) {
      mappingUpdates.scope_item_id = deleteField();
      mappingUpdates.scope_item_label = deleteField();
    }

    if (Object.keys(mappingUpdates).length > 0) {
      mappingUpdates.updated_at = Timestamp.now();
      updates.push({ ref: mappingDoc.ref, updates: mappingUpdates });
    }
  }

  return { updates, skippedSearchKeyMappingIds };
};

/** 辞書変換マッピングの更新内容を集める。辞書は反映先のみを扱う */
const collectDictionaryUpdates = async (
  patternId: string,
  isOrphaned: IsOrphaned,
  companyId: string,
): Promise<CollectResult> => {
  const snapshot = await getDocs(
    query(collectionGroup(db, 'dictionary_mappings'), where('pattern_id', '==', patternId)),
  );

  const updates: PendingUpdate[] = [];
  const skippedSearchKeyMappingIds: string[] = [];

  for (const mappingDoc of snapshot.docs) {
    const data = mappingDoc.data();

    if (data.status === masterStatus.deleted) continue;
    if (!(await belongsToCompany(mappingDoc.ref, companyId))) continue;

    // 照合元項目が孤児の場合もマスタ側と同様に記録し、反映先の掃除は行う
    const searchItemId = (data.search_item_id as string | undefined) ?? '';
    if (isOrphaned(searchItemId)) {
      skippedSearchKeyMappingIds.push(mappingDoc.id);
    }

    const columnMappings = (data.column_mappings as DictionaryColumnMapping[] | undefined) ?? [];
    const nextColumnMappings = columnMappings.filter(mapping => !isOrphaned(mapping?.item_id));

    if (nextColumnMappings.length !== columnMappings.length) {
      updates.push({
        ref: mappingDoc.ref,
        updates: {
          column_mappings: nextColumnMappings,
          updated_at: Timestamp.now(),
        },
      });
    }
  }

  return { updates, skippedSearchKeyMappingIds };
};

/**
 * マッピングの親ドキュメント（マスタ／辞書）が指定企業のものか検証する。
 *
 * collectionGroup は pattern_id だけで引くため、他社のドキュメントが混ざる余地がある。
 * Firestore ルールが他社の更新を拒否するが、その拒否はバッチ全体の失敗になり
 * 自社分の解除まで適用されなくなるため、積む前に弾く。
 * 読み取り系（master/mapping/read.ts・dictionary/mapping/read.ts）と同じ検証。
 */
const belongsToCompany = async (
  mappingRef: DocumentReference,
  companyId: string,
): Promise<boolean> => {
  const parentRef = mappingRef.parent.parent;
  if (!parentRef) return false;

  const parentSnap = await getDoc(parentRef);
  if (!parentSnap.exists()) return false;

  // 親（マスタ／辞書）が論理削除済みなら照合に使われないため触らない
  if (parentSnap.get('status') === masterStatus.deleted) return false;

  return parentSnap.get('company_id') === companyId;
};

/**
 * 更新を Firestore のバッチ上限ごとに分割してコミットする。
 * 1本のバッチに上限を超えて積むと commit が失敗し、その回の解除が1件も適用されない。
 */
const commitInBatches = async (updates: PendingUpdate[]): Promise<void> => {
  for (let start = 0; start < updates.length; start += firestoreBatchLimit) {
    const chunk = updates.slice(start, start + firestoreBatchLimit);
    const batch = writeBatch(db);
    for (const { ref, updates: mappingUpdates } of chunk) {
      batch.update(ref, mappingUpdates);
    }
    await batch.commit();
  }
};
